import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import {
  EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { useDocumentTypes } from '@/features/document'
import { toApiProblem } from '@/lib/api/client'
import {
  useProChecklistRules, useProSteps, useProTemplateMutations, useProTemplates,
} from '../api'
import type { ProSubjectType } from '../types'
import { Select } from '@/components/ui/select'

/**
 * Defining PRO processes, their steps, and the matrix behind each case's checklist.
 *
 * <p>The matrix rather than a checklist per template: what a visa application needs depends
 * on nationality, designation and sponsoring establishment, and one template per combination
 * would be dozens of near-identical templates that drift apart. Every narrowing field is
 * optional — a rule with all of them blank is the base list every case gets.
 */
export function ProTemplatesPage() {
  useBreadcrumb()
  const templates = useProTemplates()
  const documentTypes = useDocumentTypes()
  const designations = useLookup<{ id: string; name: string }>('designations')
  const grades = useLookup<{ id: string; name: string }>('grades')
  const { create, update, replaceSteps, addRule, deleteRule } = useProTemplateMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const steps = useProSteps(selected ?? undefined)
  const rules = useProChecklistRules(selected ?? undefined)

  const [draft, setDraft] = useState<{
    id?: string; code: string; name: string; category: string;
    subjectType: ProSubjectType; producesDocumentTypeId: string; renewalLeadDays: string; active: boolean
  } | null>(null)
  const [stepDraft, setStepDraft] = useState<
    { seq: number; name: string; durationDays: string; requiresChecklistComplete: boolean }[] | null
  >(null)
  const [ruleDraft, setRuleDraft] = useState<
    { label: string; documentTypeId: string; nationality: string; designationId: string; gradeId: string; mandatory: boolean } | null
  >(null)
  const [error, setError] = useState<string | null>(null)

  async function saveTemplate() {
    if (!draft) return
    setError(null)
    const body = {
      code: draft.code,
      name: draft.name,
      category: draft.category || null,
      subjectType: draft.subjectType,
      producesDocumentTypeId: draft.producesDocumentTypeId || null,
      renewalLeadDays: draft.renewalLeadDays === '' ? null : Number(draft.renewalLeadDays),
      active: draft.active,
    }
    try {
      if (draft.id) await update.mutateAsync({ id: draft.id, ...body })
      else await create.mutateAsync(body)
      setDraft(null)
    } catch (caught) {
      setError(toApiProblem(caught)?.detail ?? 'That process could not be saved')
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="PRO processes"
        subtitle="The steps a government process takes, and what has to be gathered first."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                code: '', name: '', category: '', subjectType: 'EMPLOYEE',
                producesDocumentTypeId: '', renewalLeadDays: '', active: true,
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            Define a process
          </Button>
        }
      />

      <Card>
        {templates.isPending && <Skeleton className="m-4 h-32" />}
        {templates.isError && (
          <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
        )}
        {templates.isSuccess && templates.data.length === 0 && (
          <EmptyState title="No processes yet" description="Define one and give it its steps." />
        )}
        {(templates.data?.length ?? 0) > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[48rem] text-sm">
              <thead>
                <tr className="border-b border-[var(--color-border)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  <th scope="col" className="p-3 font-medium">Process</th>
                  <th scope="col" className="p-3 font-medium">About</th>
                  <th scope="col" className="p-3 font-medium">Produces</th>
                  <th scope="col" className="p-3 font-medium">Auto-renews</th>
                  <th scope="col" className="p-3 font-medium">Status</th>
                  <th scope="col" className="p-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {templates.data?.map((template) => (
                  <tr key={template.id} className="border-b border-[var(--color-border)] last:border-0">
                    <td className="p-3 font-medium text-[var(--color-heading)]">
                      {template.name}
                      {template.seeded && (
                        <span className="ms-2 text-xs font-normal text-[var(--color-muted)]">seeded</span>
                      )}
                    </td>
                    <td className="p-3 text-[var(--color-muted)]">{template.subjectType}</td>
                    <td className="p-3 text-[var(--color-muted)]">
                      {documentTypes.data?.find((t) => t.id === template.producesDocumentTypeId)?.name ?? '—'}
                    </td>
                    <td className="p-3 tabular-nums">
                      {template.renewalLeadDays == null
                        ? <span className="text-[var(--color-muted)]">by hand</span>
                        : `${template.renewalLeadDays} days ahead`}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={template.active ? 'ACTIVE' : 'INACTIVE'} />
                    </td>
                    <td className="p-3 text-right">
                      <Button
                        type="button" size="sm" variant="ghost"
                        onClick={() => setSelected(selected === template.id ? null : template.id)}
                      >
                        {selected === template.id ? 'Hide' : 'Steps & checklist'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--color-heading)]">Steps</h2>
              <Button
                type="button" size="sm" variant="outline"
                onClick={() =>
                  setStepDraft(
                    (steps.data ?? []).map((step) => ({
                      seq: step.seq,
                      name: step.name,
                      durationDays: step.durationDays?.toString() ?? '',
                      requiresChecklistComplete: step.requiresChecklistComplete,
                    })),
                  )
                }
              >
                Edit steps
              </Button>
            </div>
            <ol className="space-y-1.5 text-sm">
              {(steps.data ?? []).map((step) => (
                <li key={step.id} className="flex flex-wrap items-baseline gap-2">
                  <span className="text-[var(--color-heading)]">{step.seq}. {step.name}</span>
                  {step.durationDays != null && (
                    <span className="text-xs text-[var(--color-muted)]">{step.durationDays} d</span>
                  )}
                  {step.requiresChecklistComplete && (
                    <span className="text-xs text-[var(--color-warning)]">needs the checklist</span>
                  )}
                  <span className="text-xs text-[var(--color-muted)]">
                    {step.typicalFee == null ? 'no fee set' : `${step.typicalFee} ${step.feeCurrency}`}
                  </span>
                </li>
              ))}
              {(steps.data?.length ?? 0) === 0 && (
                <li className="text-[var(--color-muted)]">No steps yet.</li>
              )}
            </ol>
          </Card>

          <Card className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--color-heading)]">Checklist rules</h2>
              <Button
                type="button" size="sm" variant="outline"
                onClick={() =>
                  setRuleDraft({
                    label: '', documentTypeId: '', nationality: '',
                    designationId: '', gradeId: '', mandatory: true,
                  })
                }
              >
                Add a rule
              </Button>
            </div>
            <ul className="space-y-1.5 text-sm">
              {(rules.data ?? []).map((rule) => {
                const narrowing = [
                  rule.nationality && `nationality ${rule.nationality}`,
                  rule.designationId && designations.data?.find((d) => d.id === rule.designationId)?.name,
                  rule.gradeId && grades.data?.find((g) => g.id === rule.gradeId)?.name,
                ].filter(Boolean)
                return (
                  <li key={rule.id} className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1 text-[var(--color-heading)]">
                      {rule.label
                        ?? documentTypes.data?.find((t) => t.id === rule.documentTypeId)?.name
                        ?? 'A document'}
                      <span className="ms-2 text-xs text-[var(--color-muted)]">
                        {narrowing.length > 0 ? narrowing.join(', ') : 'everybody'}
                      </span>
                    </span>
                    <Button
                      type="button" size="icon" variant="ghost"
                      aria-label={`Remove the rule for ${rule.label ?? 'a document'}`}
                      onClick={() => deleteRule.mutate(rule.id)}
                    >
                      <Trash2 className="size-4" aria-hidden="true" />
                    </Button>
                  </li>
                )
              })}
              {(rules.data?.length ?? 0) === 0 && (
                <li className="text-[var(--color-muted)]">
                  No rules — cases from this process open with an empty checklist.
                </li>
              )}
            </ul>
          </Card>
        </div>
      )}

      <FormDialog
        open={draft != null}
        onOpenChange={(next) => !next && setDraft(null)}
        title={draft?.id ? 'Edit the process' : 'Define a process'}
        loading={create.isPending || update.isPending}
        error={error}
        onSubmit={() => void saveTemplate()}
        size="lg"
      >
        {draft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="pt-name">Name</Label>
              <Input id="pt-name" required value={draft.name}
                     onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-code">Code</Label>
              <Input id="pt-code" required value={draft.code} disabled={draft.id != null}
                     onChange={(event) => setDraft({ ...draft, code: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-subject">About</Label>
              <Select id="pt-subject" value={draft.subjectType}
                      onChange={(event) => setDraft({ ...draft, subjectType: event.target.value as ProSubjectType })}
                      className="w-full">
                <option value="EMPLOYEE">A person</option>
                <option value="COMPANY">The company</option>
                <option value="CANDIDATE">A candidate (Phase 6)</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-produces">Produces</Label>
              <Select id="pt-produces" value={draft.producesDocumentTypeId}
                      onChange={(event) => setDraft({ ...draft, producesDocumentTypeId: event.target.value })}
                      className="w-full">
                <option value="">Nothing that gets filed</option>
                {(documentTypes.data ?? []).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-lead">Open a renewal case this many days ahead</Label>
              <Input id="pt-lead" type="number" min={1} value={draft.renewalLeadDays}
                     placeholder="Leave blank to start renewals by hand"
                     onChange={(event) => setDraft({ ...draft, renewalLeadDays: event.target.value })} />
              <p className="text-xs text-[var(--color-muted)]">
                Separate from the document's alert schedule: warning somebody and starting the
                work are different decisions.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pt-category">Category</Label>
              <Input id="pt-category" value={draft.category}
                     onChange={(event) => setDraft({ ...draft, category: event.target.value })} />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={stepDraft != null}
        onOpenChange={(next) => !next && setStepDraft(null)}
        title="Edit the steps"
        description="Replaces them all. Cases already open keep the copies they were opened with."
        loading={replaceSteps.isPending}
        error={error}
        size="lg"
        onSubmit={() => {
          if (!stepDraft || !selected) return
          setError(null)
          replaceSteps.mutate(
            {
              id: selected,
              steps: stepDraft.map((step, index) => ({
                seq: index + 1,
                name: step.name,
                durationDays: step.durationDays === '' ? null : Number(step.durationDays),
                requiresChecklistComplete: step.requiresChecklistComplete,
              })),
            },
            {
              onSuccess: () => setStepDraft(null),
              onError: (caught) =>
                setError(toApiProblem(caught)?.detail ?? 'Those steps could not be saved'),
            },
          )
        }}
      >
        {stepDraft && (
          <div className="space-y-3">
            {stepDraft.map((step, index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-[1fr_6rem_auto]">
                <Input
                  aria-label={`Step ${index + 1} name`}
                  required value={step.name} placeholder="What happens"
                  onChange={(event) => {
                    const next = [...stepDraft]
                    next[index] = { ...step, name: event.target.value }
                    setStepDraft(next)
                  }}
                />
                <Input
                  aria-label={`Step ${index + 1} days`}
                  type="number" min={0} value={step.durationDays} placeholder="days"
                  onChange={(event) => {
                    const next = [...stepDraft]
                    next[index] = { ...step, durationDays: event.target.value }
                    setStepDraft(next)
                  }}
                />
                <label className="flex items-center gap-1.5 text-xs">
                  <input
                    type="checkbox" checked={step.requiresChecklistComplete}
                    onChange={(event) => {
                      const next = [...stepDraft]
                      next[index] = { ...step, requiresChecklistComplete: event.target.checked }
                      setStepDraft(next)
                    }}
                  />
                  needs checklist
                </label>
              </div>
            ))}
            <Button
              type="button" variant="outline" size="sm"
              onClick={() =>
                setStepDraft([
                  ...stepDraft,
                  { seq: stepDraft.length + 1, name: '', durationDays: '', requiresChecklistComplete: false },
                ])
              }
            >
              Add a step
            </Button>
            <p className="text-xs text-[var(--color-muted)]">
              Fees are set per step and are never seeded — they vary by emirate, free zone and year.
            </p>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={ruleDraft != null}
        onOpenChange={(next) => !next && setRuleDraft(null)}
        title="Add a checklist rule"
        description="Every narrowing field is optional. Leaving them all blank means every case of this process gets the item."
        loading={addRule.isPending}
        error={error}
        onSubmit={() => {
          if (!ruleDraft || !selected) return
          setError(null)
          addRule.mutate(
            {
              id: selected,
              label: ruleDraft.label || null,
              documentTypeId: ruleDraft.documentTypeId || null,
              nationality: ruleDraft.nationality || null,
              designationId: ruleDraft.designationId || null,
              gradeId: ruleDraft.gradeId || null,
              mandatory: ruleDraft.mandatory,
            },
            {
              onSuccess: () => setRuleDraft(null),
              onError: (caught) =>
                setError(toApiProblem(caught)?.detail ?? 'That rule could not be added'),
            },
          )
        }}
      >
        {ruleDraft && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="cr-label">What must be gathered</Label>
              <Input id="cr-label" value={ruleDraft.label}
                     placeholder="Original passport collected from the employee"
                     onChange={(event) => setRuleDraft({ ...ruleDraft, label: event.target.value })} />
              <p className="text-xs text-[var(--color-muted)]">
                Most of what blocks a PRO is not a document. Use a document type below instead
                when it is one.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-document">…or a document type</Label>
              <Select id="cr-document" value={ruleDraft.documentTypeId}
                      onChange={(event) => setRuleDraft({ ...ruleDraft, documentTypeId: event.target.value })}
                      className="w-full">
                <option value="">Not a document</option>
                {(documentTypes.data ?? []).map((type) => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-nationality">Only this nationality</Label>
              <Input id="cr-nationality" maxLength={2} placeholder="Any"
                     value={ruleDraft.nationality}
                     onChange={(event) => setRuleDraft({ ...ruleDraft, nationality: event.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-designation">Only this designation</Label>
              <Select id="cr-designation" value={ruleDraft.designationId}
                      onChange={(event) => setRuleDraft({ ...ruleDraft, designationId: event.target.value })}
                      className="w-full">
                <option value="">Any designation</option>
                {(designations.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cr-grade">Only this grade</Label>
              <Select id="cr-grade" value={ruleDraft.gradeId}
                      onChange={(event) => setRuleDraft({ ...ruleDraft, gradeId: event.target.value })}
                      className="w-full">
                <option value="">Any grade</option>
                {(grades.data ?? []).map((row) => (
                  <option key={row.id} value={row.id}>{row.name}</option>
                ))}
              </Select>
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={ruleDraft.mandatory}
                     onChange={(event) => setRuleDraft({ ...ruleDraft, mandatory: event.target.checked })} />
              Mandatory — a step that needs the checklist is refused until it is gathered
            </label>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
